import { keepNewToken } from '../tokenRefresh';
/** api-lectures.ts — 업무관리 「강의 스케줄」 API (2026-09-28) */

export type LectureStatus = 'planned' | 'confirmed' | 'done' | 'canceled';
export type LectureMode = 'offline' | 'online' | 'hybrid';
export type RequesterKind = 'fp' | 'wm' | 'etc';
export type GiftStatus = 'none' | 'prepare' | 'sent';
export type LectureFileKind = 'lecture' | 'homework' | 'gift' | 'etc';

export interface LectureFile {
  name: string;
  s3_key: string;
  size: number | null;
  kind: LectureFileKind;
  uploaded_at: string;
}

export interface Lecture {
  id: string;
  title: string;
  summary: string;
  lecture_date: string | null;
  start_time: string;
  end_time: string;
  mode: LectureMode;
  place: string;
  status: LectureStatus;
  fee_manwon: number | null;
  audience_types: string[];
  audience_detail: string;
  headcount: number | null;
  requester_kind: RequesterKind;
  requester_ref_id: string | null;
  requester_name: string;
  requester_org: string;
  requester_phone: string;
  homework_text: string;
  homework_due: string | null;
  homework_sent: boolean;
  gift_item: string;
  gift_qty: number | null;
  gift_status: GiftStatus;
  gift_memo: string;
  memo: string;
  files: LectureFile[];
  created_at?: string;
  updated_at?: string;
  /** 등록한 계정 이름 (전 계정 공유 · 서버가 채움) */
  creator_name?: string | null;
}

export type LectureDraft = Omit<Lecture, 'id' | 'created_at' | 'updated_at' | 'creator_name'>;

export const emptyLecture = (): LectureDraft => ({
  title: '', summary: '', lecture_date: null, start_time: '', end_time: '',
  mode: 'offline', place: '', status: 'planned', fee_manwon: null,
  audience_types: [], audience_detail: '', headcount: null,
  requester_kind: 'etc', requester_ref_id: null, requester_name: '', requester_org: '', requester_phone: '',
  homework_text: '', homework_due: null, homework_sent: false,
  gift_item: '', gift_qty: null, gift_status: 'none', gift_memo: '',
  memo: '', files: [],
});

interface Res<T> { success: boolean; data?: T; error?: string }

function token(): string { return localStorage.getItem('token') || ''; }

async function req<T>(method: string, path: string, body?: unknown): Promise<Res<T>> {
  const opts: RequestInit = {
    method, cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
  };
  if (body !== undefined && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/tm' + path, opts);
  if (res.status === 200 || res.status === 201) {
    const nt = res.headers.get('X-New-Token');
    keepNewToken(nt);
  }
  const json = (await res.json().catch(() => ({ success: false, error: `API ${res.status}` }))) as Res<T>;
  if (!res.ok && json.success !== false) return { success: false, error: `API ${res.status}` };
  return json;
}

export interface LectureFilters { status?: 'all' | LectureStatus; period?: 'all' | 'upcoming' | 'past'; q?: string }

export function getLectures(f: LectureFilters = {}): Promise<Res<Lecture[]>> {
  const p = new URLSearchParams();
  if (f.status) p.set('status', f.status);
  if (f.period) p.set('period', f.period);
  if (f.q) p.set('q', f.q);
  p.set('_', String(Date.now()));
  return req<Lecture[]>('GET', '/lectures?' + p.toString());
}
export const createLecture = (d: LectureDraft) => req<Lecture>('POST', '/lectures', d);
export const updateLecture = (id: string, d: Partial<LectureDraft>) => req<Lecture>('PUT', `/lectures/${id}`, d);
export const deleteLecture = (id: string) => req<{ deleted: boolean }>('DELETE', `/lectures/${id}`);

/** 기존 업무관리 범용 업로드(/api/tm/upload-file) — S3 키만 받아 강의에 붙인다 */
export async function uploadLectureFile(file: File, kind: LectureFileKind): Promise<Res<LectureFile>> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/tm/upload-file', { method: 'POST', headers: { Authorization: 'Bearer ' + token() }, body: fd });
  const json = (await res.json().catch(() => ({ success: false, error: `업로드 실패 (${res.status})` }))) as Res<{ name: string; s3_key: string }>;
  if (!json.success || !json.data) return { success: false, error: json.error || '업로드 실패' };
  return { success: true, data: { name: json.data.name || file.name, s3_key: json.data.s3_key, size: file.size, kind, uploaded_at: new Date().toISOString() } };
}

export function getLectureFileUrl(f: LectureFile): Promise<Res<{ url: string }>> {
  return req<{ url: string }>('GET', `/download?key=${encodeURIComponent(f.s3_key)}&fileName=${encodeURIComponent(f.name)}`);
}
